import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { isEmbeddedInIframe } from 'utils/utils';

/**
 * Listens for `switch-ticket-success` or `refund-ticket-success` from the
 * downstroke parent and invalidates the responses cache and any open ticket's
 * details and history, so the attendees table reflects the switch or refund. Mount once at the page level where the table
 * lives. The green page's twin is `useHostTicketBridge`.
 *
 * Guards match `PunchupPageShell`'s inbound-listener discipline: the page must be
 * embedded, and the message must come from the embedding host (`window.parent`).
 * `window.postMessage` is callable by any frame on the page, so without the sender
 * check an unrelated frame could drive cache invalidation.
 */
export const useSwitchTicketBridge = () => {
	const queryClient = useQueryClient();

	useEffect(() => {
		if (!isEmbeddedInIframe()) return;

		const handler = (event: MessageEvent) => {
			if (event.source !== window.parent) return;
			const action = event.data?.action;
			if (action !== 'switch-ticket-success' && action !== 'refund-ticket-success') return;
			queryClient.invalidateQueries({ queryKey: ['responses'] });
			// What the row menu reads for an open ticket: its details and its history.
			queryClient.invalidateQueries({ queryKey: ['ticket-details'] });
			queryClient.invalidateQueries({ queryKey: ['ticket-history'] });
		};

		window.addEventListener('message', handler);
		return () => {
			window.removeEventListener('message', handler);
		};
	}, [queryClient]);
};
